/* eslint-disable no-nested-ternary */
import * as React from 'react';
import { useSelector } from 'react-redux';
import { Waypoint } from 'react-waypoint';
import { makeStyles, withStyles, Theme } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import TextField from '@material-ui/core/TextField';
import { useClickAway } from 'react-use';
import { colors } from '../../theme/theme';
import { cn, getConfig, getText, get } from '../../helpers';
import HtmlContent from '../_common/html-content';
import { ReactComponent as SearchIcon } from '../../assets/svg/icons/search.svg';
import Spinner from '../_common/spinner';
import { ReactComponent as ClearIcon } from '../../assets/svg/icons/close.svg';
import { ReactComponent as SearchAllFilterIcon } from '../../assets/svg/icons/search-all-filter.svg';
import { ReactComponent as DatasetsFilterIcon } from '../../assets/svg/icons/datasets-filter.svg';
import { ReactComponent as SolutionsFilterIcon } from '../../assets/svg/icons/solutions-filter.svg';
import { ReactComponent as BlueprintFilterIcon } from '../../assets/svg/icons/blueprint-filter.svg';
import { ReactComponent as KenshoIcon } from '../../assets/svg/icons/kensho.svg';
import { filterDatasetsSolutionsBlueprints } from '../../utilities/filter-solutions';
import { ReactComponent as CaretRightIcon } from '../../assets/svg/icons/caret-right.svg';
import { ReactComponent as QueryLibraryFilterImage } from '../../assets/svg/icons/query-library.svg';

const searchAllFilter = getText('globalSearchFilters.searchAllText');
const datasetsFilter = getText('globalSearchFilters.DatasetsText');
const solutionsFilter = getText('globalSearchFilters.SolutionsText');
const bluePrintFilter = getText('globalSearchFilters.BlueprintsText');
const queriesFilter = getText('globalSearchFilters.QueriesText');
const kenshoIconTooltip = getText('pages.llm.tooltip');

const useStyles = makeStyles(() => ({
    textField: {
        '& :before': {
            content: 'none'
        },
        '& :after': {
            content: 'none'
        }
    }
}));

const HtmlTooltip = withStyles((theme: Theme) => ({
    tooltip: {
        backgroundColor: colors.white,
        color: colors.black,
        maxWidth: theme.typography.pxToRem(400),
        fontSize: theme.typography.pxToRem(14),
        border: `${theme.typography.pxToRem(1)} solid ${colors.grey80}`
    }
}))(Tooltip);

function DatasetSearch(props: DatasetSearchProps) {
    const { searchValue, updateSearchValue, isSearching, placeholder, searchResults, lists, activeTab, onFilterChanged, urlPath, isLLMSearch, handleSearchSubmit, hideFilter, onFocusEvent } = props;
    const [showInput, setShowInput] = React.useState<boolean>(false);
    const [searchValueLocal, setSearchValueLocal] = React.useState<string>(searchValue); // we need it to avoid useless event emittments, and performance improvement
    const [searchFilterState, setSearchFilterState] = React.useState<string>('All Results');
    const [currPlaceholderIndex, setCurrPlaceholderIndex] = React.useState<number>(0);
    const searchCompRef: React.RefObject<HTMLDivElement> = React.useRef(null);
    const textFieldRef: React.RefObject<HTMLInputElement> = React.useRef(null);
    const selectBarRef: React.RefObject<HTMLInputElement> = React.useRef(null);
    const enabledBlueprintFlag = useSelector((state: RootStoreState) => get(state, 'shared.flags.enabledBlueprintFlag', false));
    const user = useSelector((state: RootStoreState) => get(state, 'user', ''));
    const isLoggedIn = user && user.isLoggedIn ? user.isLoggedIn : false;
    const [currLogoutPlaceholderIndex, setCurrLogoutPlaceholderIndex] = React.useState<number>(0);
    const [maxChars, setMaxChars] = React.useState<number>(20);
    const [maxCharErrMsg, setMaxCharErrMsg] = React.useState<string>('');
    const [isTextArea, setIsTextArea] = React.useState(false);
    const [isTabbed, setisTabbed] = React.useState(true);
    const cursorPositionRef = React.useRef<number>(0);
    const [isInitial, setIsInitial] = React.useState<boolean>(true);

    const localeCode = getText('langCode');
    const genAIPlaceholder = getText('pages.home.genAIprompt');
    const genAILogoutPlaceholder = getText('pages.home.genAILogoutprompt');
    const globalSearchPage = window.location.href.includes('search-results');

    const toggleInput = (value: boolean) => {
        // we need this timeout to handle another events in scope of search bar
        if (showInput) {
            setTimeout(() => {
                setShowInput(value);
            }, 100);
        } else {
            setShowInput(value);
            setTimeout(() => {
                const inp: any = textFieldRef.current;
                if (inp) inp.click();
            }, 100);
        }
    };

    React.useEffect(() => {
        if (searchValue.length === 0) {
            setIsTextArea(false);
        }
        if (searchValue !== searchValueLocal) setSearchValueLocal(searchValue);
    }, [searchValue]);

    React.useEffect(() => {
        const interval = setInterval(() => {
            const element = document.getElementById('dataset-search__placeholder');
            if (element && !isLoggedIn && urlPath && urlPath.includes('search-results')) {
                element.classList.remove('text-show');
                element.classList.add('text-fade');
                if (genAILogoutPlaceholder) {
                    setTimeout(() => {
                        setCurrLogoutPlaceholderIndex((prev => (prev + 1) % genAILogoutPlaceholder.length));
                        element.classList.remove('text-fade');
                        element.classList.add('text-show');
                    }, 1000);
                }
            } else {
                setCurrLogoutPlaceholderIndex(0);
            }
        }, 4000);
        return () => clearInterval(interval);
    }, []);

    const handleBlur = () => {
        setTimeout(() => {
            setIsTextArea(false);
        }, 100);
    };

    const getLogoutPlaceholder = () => {
        if (isLoggedIn) return placeholder;
        return genAILogoutPlaceholder && genAILogoutPlaceholder[currLogoutPlaceholderIndex];
    };

    const resetSearchValue = () => {
        updateSearchValue('');
        setMaxCharErrMsg('');
        setCurrLogoutPlaceholderIndex(0);
        toggleInput(false);
        handleBlur();
    };

    const keyPressHandler = (event: KeyboardEvent) => {
        let itemId = '';
        if (event.key === 'Enter') {
            setShowInput(false);
            if (event.target instanceof HTMLElement) {
                itemId = event.target.getAttribute('id') || '';
            }
            if (itemId && itemId === 'clearicon-btn') {
                resetSearchValue();
            }
            if (itemId && itemId === 'data-search-bar') {
                if (handleSearchSubmit && !(maxCharErrMsg.length > 0)) handleSearchSubmit(true);
            }
        }

        if (event.key === 'Escape') {
            const element = document.getElementById('clearicon-btn');
            if (element) element.click();
        }
        if (!isLLMSearch && event.key === 'Tab' && urlPath && urlPath.includes('search-results')) {
            if (event.target instanceof Element) {
                itemId = event.target.getAttribute('id') || '';
            }
            if (itemId && itemId === 'data-search-bar' && !event.shiftKey) {
                setTimeout(() => {
                    const d5 = document.getElementById('clearicon-btn');
                    if (d5) d5.focus();
                }, 100);
                event.preventDefault();
                if (searchFilterState === 'All Results') {
                    const d = document.getElementById('all-result-btn');
                    if (d) d.focus();
                }
                if (searchFilterState === 'Datasets') {
                    const d1 = document.getElementById('dataset-btn');
                    if (d1) d1.focus();
                }
                if (searchFilterState === 'Solutions') {
                    const d2 = document.getElementById('solution-btn');
                    if (d2) d2.focus();
                }
                if (searchFilterState === 'Blueprints') {
                    const d3 = document.getElementById('clearicon-btn');
                    if (d3) d3.focus();
                }
            }
            if (itemId && itemId === 'blueprint-btn' && !event.shiftKey) {
                event.stopPropagation();
                event.preventDefault();
                const d3 = document.getElementById('all-result-btn');
                if (d3) d3.focus();
            }
            if (itemId && itemId === 'all-result-btn' && event.shiftKey) {
                event.preventDefault();
                const blpBtn = document.getElementById('blueprint-btn');
                if (blpBtn) blpBtn.focus();
            }
        }
        if (event.target instanceof Element) {
            itemId = event.target.getAttribute('id') || '';
        }
        setisTabbed(true);
        if (itemId && itemId === 'data-search-bar' && event.key === 'Tab') {
            setisTabbed(false);
        }
        if (itemId && itemId === 'kenshoIcon' && !event.shiftKey) {
            toggleInput(true);
            setisTabbed(true);
            const element = document.getElementById('data-search-bar-btn');
            if (element) {
                element.focus();
                element.click();
            }
            setTimeout(() => {
                const d = document.getElementById('data-search-bar');
                if (d) d.focus();
            }, 100);
        }
        if (itemId && itemId === 'clearicon-btn' && event.shiftKey) {
            toggleInput(true);
            setisTabbed(true);
            const element = document.getElementById('data-search-bar-btn');
            if (element) {
                element.focus();
                element.click();
            }
            setTimeout(() => {
                const d = document.getElementById('data-search-bar');
                if (d) d.focus();
            }, 100);
        }

        if (itemId && itemId === 'clearicon-btn' && event.key === 'Tab') {
            toggleInput(false);
        }
    };

    React.useEffect(() => {
        if (urlPath) { // && !urlPath.includes('search-results')
            if (activeTab === 1) setSearchFilterState('Datasets');
            else if (activeTab === 2) setSearchFilterState('Solutions');
            else if (activeTab === 3) setSearchFilterState('Blueprints');
            else if (activeTab === 4) setSearchFilterState('Queries');
            else setSearchFilterState('All Results');
        }
    }, [activeTab]);

    React.useEffect(() => {
        const container = searchCompRef ? searchCompRef.current : null;
        if (container) container.addEventListener('keydown', keyPressHandler);
        return () => {
            if (container) {
                container.removeEventListener('keydown', keyPressHandler);
            }
        };
    });

    React.useEffect(() => {
        if (textFieldRef.current) {
            textFieldRef.current.setSelectionRange(cursorPositionRef.current, cursorPositionRef.current);
        }
    }, [searchValue]);

    const filterBtnIconFill = (isActive: boolean) => {
        return isActive ? '#D6002A' : '#515151';
    };

    // we need this timeout to not trigger search, if user is clickeng several times
    let tm: any;
    const handleFilterChanged = (filterState: string) => {
        if (tm) clearTimeout(tm);
        setSearchFilterState(filterState);
        tm = setTimeout(() => {
            let selTab = 0;
            if (filterState === 'Datasets') selTab = 1;
            if (filterState === 'Solutions') selTab = 2;
            if (filterState === 'Blueprints') selTab = 3;
            const scrollableWrapper = document.getElementsByClassName('dataset-search__searchfilterbar')[0];
            if (filterState === 'All Results' && scrollableWrapper) {
                scrollableWrapper.scrollLeft -= 100;
            }
            if (filterState === 'Blueprints' && scrollableWrapper) {
                scrollableWrapper.scrollLeft += 100;
            }
            if (onFilterChanged && !isLLMSearch) onFilterChanged(selTab);
        }, 500);
    };

    const getCount = (value: string) => {
        if (value === 'Datasets') {
            const result = lists || [];
            const dataset = filterDatasetsSolutionsBlueprints(result);
            return dataset.length;
        }
        if (value === 'Solutions') {
            const res = lists || [];
            const solution = filterDatasetsSolutionsBlueprints(res, true);
            return solution.length;
        }
        if (value === 'Blueprints') {
            const res = lists || [];
            const bluePrint = filterDatasetsSolutionsBlueprints(res, false, true);
            return bluePrint.length;
        }
        return (lists || []).length;
    };

    const handleOnChange = (event: React.ChangeEvent) => {
        const { value, selectionStart } = event.currentTarget as HTMLInputElement;
        cursorPositionRef.current = selectionStart || 0;
        const regex = /^\s+/g;
        setSearchValueLocal(value.replace(regex, ''));
        const newSearch = value.replace(regex, '');
        if (newSearch.length > 500) {
            setMaxCharErrMsg('The maximum 500 characters has been exceeded.  Please shorten your query.');
        } else if (maxCharErrMsg.length > 0) setMaxCharErrMsg('');

    };

    React.useEffect(() => {
        const timeDuration = (globalSearchPage && !isInitial) ? 0 : 500;
        const timer = setTimeout(() => {
            const regex = /^\s+/g;
            if (handleSearchSubmit) handleSearchSubmit(false);
            if (searchValueLocal !== undefined && !isInitial) {
                updateSearchValue(searchValueLocal);
            } else {
                setIsInitial(false);
            }
        }, timeDuration);
        return () => clearTimeout(timer);
    }, [searchValueLocal]);

    const checkContentWidth = (element: Element) => {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');

        if (context && element) {
            const inputElement = element as HTMLElement;
            context.font = getComputedStyle(inputElement).font;
            const contentWidth = context.measureText(searchValue).width;
            return contentWidth > (inputElement.offsetWidth - 40);
        }
        return false;
    };

    const activateTextAreaOrInput = (isContentWidthExceeded: boolean) => {
        const focusElement = () => {
            setTimeout(() => {
                const inp: any = textFieldRef.current;
                if (inp) {
                    const l: any = inp.value.length;
                    inp.focus();
                    inp.setSelectionRange(l, l);
                }
            }, 100);
        };
        if (isContentWidthExceeded) {
            setIsTextArea(true);
            focusElement();
        } else if (isTextArea) {
            setIsTextArea(false);
            focusElement();
        }
    };

    const handleScroll = (w: Waypoint.CallbackArgs) => {
        if (w.viewportTop > w.waypointTop) {
            if (showInput) toggleInput(false);
        }
    };

    const classes = useStyles();

    const clearSearchValue = (e: React.MouseEvent) => {
        e.stopPropagation();
        resetSearchValue();
    };


    const handleKeyEvent = (event: React.KeyboardEvent) => {
        if (event.keyCode === 8) {
            toggleInput(true);
            setisTabbed(true);
            const element = document.getElementById('data-search-bar-btn');
            if (element) element.focus();
        }
    };

    const handleOnFocus = (event: React.FocusEvent) => {
        setCurrPlaceholderIndex(0);
        let itemId = '';
        let isContentWidthExceeded = false;
        if (event.target instanceof HTMLElement) {
            itemId = event.target.getAttribute('id') || '';
        }
        if (itemId && itemId === 'data-search-bar') {
            isContentWidthExceeded = checkContentWidth(event.target);
        }
        activateTextAreaOrInput(isContentWidthExceeded);
        if (onFocusEvent) onFocusEvent(event);
    };

    function filterOptionAllresult() {
        return (
            <>
                <div className={`dataset-search__searchfilterbar ${localeCode === 'ja' ? 'jaLang' : ''}`}>
                    <div role="button" id="all-result-btn" className={getCount('All Results') === 0 ? 'dataset-search__disabled' : 'dataset-search__enabled'} onClick={() => handleFilterChanged('All Results')} onFocus={() => { handleFilterChanged('All Results'); }} onKeyUp={e => { handleKeyEvent(e); }} tabIndex={0} data-testid="search-all">
                        <div className={`${searchFilterState === 'All Results' ? 'dataset-search__searchfilterbtn__enable icon' : 'dataset-search__searchfilterbtn__inactive icon'}`}>
                            <SearchAllFilterIcon className="icon" fill={filterBtnIconFill(searchFilterState === 'All Results')} />
                            <div className="dataset-search__searchfilterbar__searchFiltersText">
                                {searchAllFilter} {getCount('All Results')}
                            </div>
                        </div>
                    </div>
                    <div role="button" id="dataset-btn" className={getCount('Datasets') === 0 ? 'dataset-search__disabled' : 'dataset-search__enabled'} onClick={() => handleFilterChanged('Datasets')} onFocus={() => { handleFilterChanged('Datasets'); }} onKeyUp={e => { handleKeyEvent(e); }} tabIndex={0} data-testid="search-dataset">
                        <div className={`${searchFilterState === 'Datasets' ? 'dataset-search__searchfilterbtn__enable icon' : 'dataset-search__searchfilterbtn__inactive icon'}`}>
                            <DatasetsFilterIcon className="icon" fill={filterBtnIconFill(searchFilterState === 'Datasets')} />
                            <div className="dataset-search__searchfilterbar__searchFiltersText">
                                {datasetsFilter} {getCount('Datasets')}
                            </div>
                        </div>
                    </div>
                    <div role="button" id="solution-btn" className={getCount('Solutions') === 0 ? 'dataset-search__disabled' : 'dataset-search__enabled'} onClick={() => handleFilterChanged('Solutions')} onFocus={() => { handleFilterChanged('Solutions'); }} onKeyUp={e => { handleKeyEvent(e); }} tabIndex={0} data-testid="search-solution">
                        <div className={`${searchFilterState === 'Solutions' ? 'dataset-search__searchfilterbtn__enable icon' : 'dataset-search__searchfilterbtn__inactive icon'}`}>
                            <SolutionsFilterIcon className="icon" fill={filterBtnIconFill(searchFilterState === 'Solutions')} />
                            <div className="dataset-search__searchfilterbar__searchFiltersText">
                                {solutionsFilter} {getCount('Solutions')}
                            </div>
                        </div>
                    </div>
                    {enabledBlueprintFlag && (
                        <div role="button" id="blueprint-btn" className={getCount('Blueprints') === 0 ? 'dataset-search__disabled' : 'dataset-search__enabled'} onClick={() => handleFilterChanged('Blueprints')} onFocus={() => { handleFilterChanged('Blueprints'); }} onKeyUp={e => { handleKeyEvent(e); }} tabIndex={0} data-testid="search-blueprint">
                            <div className={`${searchFilterState === 'Blueprints' ? 'dataset-search__searchfilterbtn__enable icon' : 'dataset-search__searchfilterbtn__inactive icon'}`}>
                                <BlueprintFilterIcon className="icon" fill={filterBtnIconFill(searchFilterState === 'Blueprints')} />
                                <div className="dataset-search__searchfilterbar__searchFiltersText">
                                    {bluePrintFilter} {getCount('Blueprints')}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                <div className="dataset-search__searchfilterbar--a" />
            </>
        );
    }
    function filterOptionDatasets() {
        return (
            <div className={`dataset-search__searchfilterbar dataset-search__filter-mrgn-dataset-sol ${localeCode === 'ja' ? 'jaLang' : ''}`}>
                <div role="button" id="dataset-btn" className={getCount('Datasets') === 0 ? 'dataset-search__disabled' : 'dataset-search__enabled'} onKeyUp={() => {}} tabIndex={-1}>
                    <div className={`${searchFilterState === 'Datasets' ? 'dataset-search__searchfilterbtn__enable icon' : 'dataset-search__searchfilterbtn__inactive icon'}`}>
                        <DatasetsFilterIcon className="icon" fill={filterBtnIconFill(searchFilterState === 'Datasets')} />
                        <div className="dataset-search__searchfilterbar__searchFiltersText">
                            {datasetsFilter} {getCount('Datasets')}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    function filterOptionSolutions() {
        return (
            <div className={`dataset-search__searchfilterbar dataset-search__filter-mrgn-dataset-sol ${localeCode === 'ja' ? 'jaLang' : ''}`}>
                <div role="button" id="solution-btn" className={getCount('Solutions') === 0 ? 'dataset-search__disabled' : 'dataset-search__enabled'} onKeyUp={() => {}} tabIndex={-1}>
                    <div className={`${searchFilterState === 'Solutions' ? 'dataset-search__searchfilterbtn__enable icon' : 'dataset-search__searchfilterbtn__inactive icon'}`}>
                        <SolutionsFilterIcon className="icon" fill={filterBtnIconFill(searchFilterState === 'Solutions')} />
                        <div className="dataset-search__searchfilterbar__searchFiltersText">{solutionsFilter} {getCount('Solutions')}</div>
                    </div>
                </div>
            </div>
        );

    }

    function filterOptionBlueprints() {
        return (
            <div className={`dataset-search__searchfilterbar dataset-search__filter-mrgn-dataset-sol ${localeCode === 'ja' ? 'jaLang' : ''}`}>
                <div role="tab" id="blueprint-btn" className={getCount('Blueprints') === 0 ? 'dataset-search__disabled' : 'dataset-search__enabled'} onKeyUp={() => {}} tabIndex={-1} aria-hidden="true">
                    <div className={`${searchFilterState === 'Blueprints' ? 'dataset-search__searchfilterbtn__enable icon' : 'dataset-search__searchfilterbtn__inactive icon'}`}>
                        <BlueprintFilterIcon className="icon" fill={filterBtnIconFill(searchFilterState === 'Blueprints')} tabIndex={-1} aria-hidden="true" />
                        <div className="dataset-search__searchfilterbar__searchFiltersText">{bluePrintFilter} {getCount('Blueprints')}</div>
                    </div>
                </div>
            </div>
        );

    }

    function filterOptionQueries() {
        return (
            <div className={`dataset-search__searchfilterbar dataset-search__filter-mrgn-dataset-sol ${localeCode === 'ja' ? 'jaLang' : ''}`}>
                <div role="tab" id="queries-btn" className={getCount('Queries') === 0 ? 'dataset-search__disabled' : 'dataset-search__enabled'} onKeyUp={() => {}} tabIndex={-1} aria-hidden="true">
                    <div className={`${searchFilterState === 'Queries' ? 'dataset-search__searchfilterbtn__enable icon' : 'dataset-search__searchfilterbtn__inactive icon'}`}>
                        <QueryLibraryFilterImage className="icon" fill={filterBtnIconFill(searchFilterState === 'Queries')} tabIndex={-1} aria-hidden="true" />
                        <div className="dataset-search__searchfilterbar__searchFiltersText">{queriesFilter} {getCount('Queries')}</div>
                    </div>
                </div>
            </div>
        );

    }

    React.useEffect(() => {
        const interval = setInterval(() => {
            const element = document.getElementById('dataset-search__placeholder');
            if (element && element !== document.activeElement && urlPath && urlPath.includes('search-results')) {
                element.classList.remove('text-show');
                element.classList.add('text-fade');
                if (genAIPlaceholder) {
                    setTimeout(() => {
                        setCurrPlaceholderIndex((prev => (prev + 1) % genAIPlaceholder.length));
                        element.classList.remove('text-fade');
                        element.classList.add('text-show');
                    }, 1000);
                }
            } else {
                setCurrPlaceholderIndex(0);
            }
        }, 4000);
        return () => clearInterval(interval);
    }, []);

    const getPlaceholder = () => {
        return genAIPlaceholder && genAIPlaceholder[currPlaceholderIndex];
    };

    // max number of characters till input text overflow
    // 6.5 is estimated avarage char width for current font size
    const updateMaxChars = () => {
        if (searchCompRef.current) {
            const inp: any = searchCompRef.current;
            const estimatedMaxChars = Math.floor(inp.offsetWidth) / 6.5 - 10;
            setMaxChars(estimatedMaxChars);
        }
    };

    React.useEffect(() => {
        updateMaxChars();
    });

    useClickAway(selectBarRef, () => toggleInput(false));

    const focusSearchBar = (event: React.KeyboardEvent) => {
        if (event.keyCode === 9) {
            toggleInput(true);
            const element = document.getElementById('data-search-bar-btn');
            if (element) {
                element.focus();
                element.click();
            }
            setTimeout(() => {
                const d = document.getElementById('data-search-bar');
                if (d) d.focus();
            }, 100);
        }
    };


    const showToolTipIcon = () => {
        return (
            <div
                onClick={() => { setShowInput(false); }}
                role="presentation"
                className="dataset-search__logo-wrap"
            >
                <HtmlTooltip
                    interactive
                    placement="top"
                    arrow
                    enterTouchDelay={0}
                    leaveTouchDelay={4000}
                    tabIndex={0}
                    aria-label="info tooltip"
                    role="button"
                    className="react-tooltip-clickable-link"
                    title={
                        <HtmlContent className="tooltip-content" html={kenshoIconTooltip} />
                    }
                >
                    <KenshoIcon tabIndex={0} id="kenshoIcon" className="dataset-search__kenshoicon" />
                </HtmlTooltip>
            </div>
        );
    };

    return (
        <div className="dataset-search page-section" ref={searchCompRef}>
            <div className="dataset-search__wrapcomp page-section__inner">
                <div className={`dataset-search__bar ${localeCode === 'ja' ? 'jaLang' : ''} ${isTextArea && 'textarea-state'} ${urlPath && urlPath.includes('search-results') ? 'search-res-page' : ''}`} ref={selectBarRef}>
                    {urlPath && urlPath.includes('search-results') && showToolTipIcon()}
                    {showInput ? (
                        <Waypoint onLeave={(w: Waypoint.CallbackArgs) => handleScroll(w)}>
                            <TextField
                                type="search"
                                id="data-search-bar"
                                autoComplete="off"
                                autoFocus={isTabbed}
                                fullWidth
                                onChange={handleOnChange}
                                value={searchValueLocal}
                                multiline={isTextArea}
                                onFocus={handleOnFocus}
                                onBlur={handleBlur}
                                placeholder={isLoggedIn && urlPath && urlPath.includes('search-results') && genAIPlaceholder && genAIPlaceholder[0]}
                                className={cn(classes.textField, 'dataset-search__input')}
                                inputRef={textFieldRef}
                                inputProps={{
                                    'data-testid': 'dataset-search-input',
                                }}
                            />
                        </Waypoint>
                    ) : (
                        <button type="button" onFocus={() => toggleInput(true)} onClick={() => toggleInput(true)} className={`dataset-search__button ${urlPath && urlPath.includes('search-results') ? 'search-res-page' : ''}`} data-testid="search-button" id="data-search-bar-btn">
                            {searchValue && searchValue.length ? (
                                <p className="search-result-summary" data-testid="search-result-summary">
                                    {searchResults}
                                    &#8220;
                                    <span>{searchValue}</span>
                                    &#8221;
                                </p>
                            ) : (
                                <>
                                    {!(urlPath && urlPath.includes('search-results')) ? (
                                        <p className="dataset-search__placeholder" data-testid="search-placeholder" id="dataset-search__placeholder">
                                            {placeholder}
                                        </p>
                                    ) : isLoggedIn ? (
                                        <p className="dataset-search__placeholder" data-testid="search-placeholder" id="dataset-search__placeholder">
                                            {getPlaceholder()}
                                        </p>
                                    ) : (
                                        <p className="dataset-search__placeholder" data-testid="search-placeholder" id="dataset-search__placeholder">
                                            {getLogoutPlaceholder()}
                                        </p>
                                    )}
                                </>
                            )}
                        </button>
                    )}
                    { !isLLMSearch && showInput && <Spinner disable={!isSearching} />}
                    {(!showInput || (showInput && !searchValue.length)) ? (
                        <div
                            className={cn('dataset-search__icon')}
                        >
                            <SearchIcon />
                        </div>
                    ) : (
                        <div className="dataset-search__tab">
                            <button
                                onClick={clearSearchValue}
                                type="button"
                                className={cn('button button--icon-only dataset-search__clearicon', isLLMSearch ? 'dataset-search__clearicon-red' : '')}
                                aria-label="clear-icon"
                                id="clearicon-btn"
                                data-testid="dataset-search-clearicon-btn"
                                tabIndex={0}
                            >
                                <ClearIcon />
                            </button>
                        </div>
                    )}
                </div>
                {maxCharErrMsg && maxCharErrMsg.length > 0 && (<span className="dataset-search__error-message">{maxCharErrMsg}</span>)}
                {!hideFilter && urlPath && urlPath.includes('search-results') &&
                    (filterOptionAllresult())}
                {!hideFilter && urlPath && urlPath.includes('datasets') &&
                    filterOptionDatasets()}
                {!hideFilter && urlPath && urlPath.includes('solutions') &&
                    filterOptionSolutions()}
                {!hideFilter && enabledBlueprintFlag && urlPath && urlPath.includes('blueprints') &&
                    filterOptionBlueprints()}
                {!hideFilter && urlPath && urlPath.includes('query-library') &&
                    filterOptionQueries()}
            </div>
            {!hideFilter && (
                <hr className="dataset-search__bottomline" />
            )}
        </div>
    );
}

export default React.memo(DatasetSearch);
